import { clsx } from 'clsx'
import { CheckCircle2, AlertCircle, Info } from 'lucide-react'
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

type Toast = { id: number; kind: 'success' | 'error' | 'info'; text: string }
const Ctx = createContext<{ push: (kind: Toast['kind'], text: string) => void } | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([])
  const push = useCallback((kind: Toast['kind'], text: string) => {
    const id = Date.now() + Math.random()
    setItems((s) => [...s, { id, kind, text }])
    setTimeout(() => setItems((s) => s.filter((t) => t.id !== id)), 4500)
  }, [])
  const value = useMemo(() => ({ push }), [push])
  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
        {items.map((t) => (
          <div key={t.id} className={clsx('pointer-events-auto animate-slide-in-right flex items-center gap-2.5 rounded-lg border bg-white px-3.5 py-2.5 text-sm shadow-(--shadow-surface)',
            t.kind === 'success' && 'border-success-500/30 text-success-600', t.kind === 'error' && 'border-danger-500/30 text-danger-600', t.kind === 'info' && 'border-line-200 text-ink-700')}>
            {t.kind === 'success' ? <CheckCircle2 className="size-4" /> : t.kind === 'error' ? <AlertCircle className="size-4" /> : <Info className="size-4" />}
            {t.text}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  )
}

export function useToast() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useToast outside ToastProvider')
  return ctx
}
